import { ref, computed } from 'vue'

const userData = ref(JSON.parse(localStorage.getItem('user') || '{}'))

// Role hierarchy and permissions
const roleHierarchy = {
  admin: ['admin', 'manager', 'tester', 'viewer'],
  manager: ['manager', 'tester', 'viewer'],
  tester: ['tester', 'viewer'],
  viewer: ['viewer']
}

const rolePermissions = {
  admin: ['create', 'read', 'update', 'delete', 'manage_users'],
  manager: ['create', 'read', 'update', 'manage_team'],
  tester: ['create', 'read', 'update'],
  viewer: ['read']
}

export const useUserStore = () => {
  const updateUser = (newData) => {
    userData.value = { ...userData.value, ...newData }
    localStorage.setItem('user', JSON.stringify(userData.value))
  }

  const userInitials = computed(() => {
    if (userData.value.firstName && userData.value.lastName) {
      return `${userData.value.firstName[0]}${userData.value.lastName[0]}`
    }
    return (userData.value.email?.split('@')[0] || 'U').substring(0, 2).toUpperCase()
  })

  const userName = computed(() => {
    if (userData.value.firstName && userData.value.lastName) {
      return `${userData.value.firstName} ${userData.value.lastName}`
    }
    return userData.value.email?.split('@')[0] || 'User'
  })

  // Role-based computed properties
  const isAdmin = computed(() => userData.value.role === 'admin')
  const isManager = computed(() => userData.value.role === 'manager')
  const isTester = computed(() => userData.value.role === 'tester')
  const isViewer = computed(() => userData.value.role === 'viewer')

  // Permission checking methods
  const hasRole = (role) => {
    if (!userData.value) {
      console.warn('hasRole called with no userData in userStore');
      return false;
    }
    
    if (!userData.value.role) {
      console.warn(`User has no role defined. User: ${JSON.stringify(userData.value)}`);
      return false;
    }
    
    // In case of admin, always return true regardless of hierarchy
    if (userData.value.role === 'admin') {
      return true;
    }
    
    return roleHierarchy[userData.value.role]?.includes(role) || false;
  }

  const hasPermission = (permission) => {
    return rolePermissions[userData.value.role]?.includes(permission) || false
  }

  const canCreate = computed(() => hasPermission('create'))
  const canUpdate = computed(() => hasPermission('update'))
  const canDelete = computed(() => hasPermission('delete'))
  const canManageUsers = computed(() => hasPermission('manage_users'))
  const canManageTeam = computed(() => hasPermission('manage_team'))

  return {
    userData,
    userInitials,
    userName,
    updateUser,
    // Role checks
    isAdmin,
    isManager,
    isTester,
    isViewer,
    hasRole,
    // Permission checks
    hasPermission,
    canCreate,
    canUpdate,
    canDelete,
    canManageUsers,
    canManageTeam
  }
}
